import { useEffect, useState } from 'react';
import { ChevronDown, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useConsentContent } from '@/modules/consent/hooks/use-consent-content';
import { type ConsentChoices, useConsentChoices } from '@/modules/consent/hooks/use-consent-choices';

function CategoryRow({
    category,
    enabled,
    onToggle,
}: {
    category: { key: string; title: string; description: string; is_required: boolean };
    enabled: boolean;
    onToggle: (checked: boolean) => void;
}) {
    const [open, setOpen] = useState(false);

    return (
        <div className="border-b last:border-b-0">
            <div className="flex w-full items-center justify-between gap-3 py-3">
                <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => setOpen((o) => !o)}>
                    <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
                    <span className="text-sm font-semibold">{category.title}</span>
                </button>
                {category.is_required ? (
                    <span className="shrink-0 text-xs font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                        Always Active
                    </span>
                ) : (
                    <Switch checked={enabled} onCheckedChange={onToggle} />
                )}
            </div>
            {open && <p className="pb-3 pl-6 text-xs leading-relaxed text-muted-foreground">{category.description}</p>}
        </div>
    );
}

export function ConsentPreferencesDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
    const { data, isLoading } = useConsentContent();
    const { choices, save } = useConsentChoices();
    const [draft, setDraft] = useState<ConsentChoices>(choices);

    useEffect(() => {
        if (open) setDraft(choices);
    }, [open, choices]);

    function allChoices(value: boolean): ConsentChoices {
        const next: ConsentChoices = {};
        for (const cat of data?.categories ?? []) next[cat.key] = cat.is_required ? true : value;
        return next;
    }

    function handleSave(next: ConsentChoices) {
        save(next);
        onOpenChange(false);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShieldCheck className="size-4 text-primary" />
                        Privacy Preferences
                    </DialogTitle>
                </DialogHeader>

                {isLoading && (
                    <div className="space-y-2">
                        <Skeleton className="h-20 w-full" />
                        <Skeleton className="h-32 w-full" />
                    </div>
                )}

                {!isLoading && data && (
                    <>
                        <div className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground">{data.intro_text}</div>

                        <div className="rounded-lg border px-3">
                            {data.categories
                                .slice()
                                .sort((a, b) => a.sort_order - b.sort_order)
                                .map((category) => (
                                    <CategoryRow
                                        key={category.key}
                                        category={category}
                                        enabled={category.is_required || (draft[category.key] ?? false)}
                                        onToggle={(checked) => setDraft((d) => ({ ...d, [category.key]: checked }))}
                                    />
                                ))}
                        </div>
                    </>
                )}

                <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleSave(allChoices(false))}>
                            Reject All
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleSave(allChoices(true))}>
                            Allow All
                        </Button>
                    </div>
                    <Button size="sm" onClick={() => handleSave(draft)}>
                        Save Preferences
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
